/**
 * SlidesLayout Component
 * Alternative PowerPoint-style layout for multi-scene recording
 * Three-panel layout: slides rail (left), canvas + notes (center), scene details (right)
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';
import { 
  ChevronLeft, 
  ChevronRight 
} from 'lucide-react';
import {
  sceneListAtom,
  activeProjectAtom,
  selectedSceneIdAtom,
  currentSceneAtom,
  isTeleprompterOpenAtom
} from '../../atoms/sceneAtoms';
import { SlidesRail } from './SlidesRail';
import { SpeakerNotes } from './SpeakerNotes';
import { SceneDetailsPanel } from './SceneDetailsPanel';
import logger from '../../utils/logger';
import './SlidesLayout.scss';

interface SlidesLayoutProps {
  canvasComponent: React.ReactNode;
  recordingControls: React.ReactNode;
  canvasRatio?: string;
  liveBackgroundColor?: string | null;
  liveBackgroundImage?: HTMLImageElement | null;
  isUpdatingScene?: boolean;
  savingTransitionSceneId?: string | null;
  onProjectTitleChange?: (projectId: string, newTitle: string) => void;
  onAddScene?: () => void;
  onRenameScene?: (sceneId: string) => void;
  onDuplicateScene?: (sceneId: string) => void;
  onDeleteScene?: (sceneId: string) => void;
  onReorderScenes?: (sceneId: string, newIndex: number) => void;
  onSceneTitleChange?: (sceneId: string, newTitle: string) => void;
  onSceneTagsChange?: (sceneId: string, tags: any[]) => void;
  onSceneNotesChange?: (sceneId: string, notes: string) => void;
  onCopySettings?: (sceneId: string) => void;
  onJumpToRecording?: (sceneId: string) => void;
  onRetakeScene?: (sceneId: string) => void;
  onEditSettings?: (sceneId: string) => void;
  onExportScene?: (sceneId: string) => void;
  onResetVideo?: (sceneId: string) => void;
  onPreviewScene?: (sceneId: string) => void;
  onConfirmScene?: (sceneId: string) => void;
  onEditTransition?: (fromSceneIndex: number, toSceneIndex: number) => void;
  onPreviewTransition?: (fromSceneIndex: number, toSceneIndex: number) => void;
  onResizeComplete?: () => void;
  onResetBackground?: () => void;
}

export const SlidesLayout: React.FC<SlidesLayoutProps> = ({
  canvasComponent,
  recordingControls,
  canvasRatio = '16:9',
  liveBackgroundColor,
  liveBackgroundImage,
  isUpdatingScene = false,
  savingTransitionSceneId,
  onProjectTitleChange,
  onAddScene,
  onRenameScene,
  onDuplicateScene,
  onDeleteScene,
  onReorderScenes,
  onSceneTitleChange,
  onSceneTagsChange,
  onSceneNotesChange,
  onCopySettings,
  onJumpToRecording,
  onRetakeScene,
  onEditSettings,
  onExportScene,
  onResetVideo,
  onPreviewScene,
  onConfirmScene,
  onEditTransition,
  onPreviewTransition,
  onResizeComplete,
  onResetBackground,
}) => {
  const [sceneList] = useAtom(sceneListAtom);
  const [activeProject] = useAtom(activeProjectAtom);
  const [selectedSceneId, setSelectedSceneId] = useAtom(selectedSceneIdAtom);
  const [currentScene, setCurrentScene] = useAtom(currentSceneAtom);
  const [, setIsTeleprompterOpen] = useAtom(isTeleprompterOpenAtom);

  const [isLeftSidebarCollapsed, setIsLeftSidebarCollapsed] = useState(false);
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(false);
  
  // Handler for opening teleprompter
  const handleOpenTeleprompter = useCallback(() => {
    setIsTeleprompterOpen(true);
  }, [setIsTeleprompterOpen]);
  
  // Resizable canvas/notes state
  const NAVBAR_HEIGHT = 64; // Main navbar height
  const TOOLBAR_HEIGHT = 80; // Recording toolbar height (static)
  const CANVAS_MIN_PERCENT = 40; // Canvas must be at least 40% of available height
  const NOTES_MIN_HEIGHT = 150; // Speaker notes minimum height in pixels
  const DEFAULT_NOTES_HEIGHT = 150; // Default speaker notes height in pixels
  
  // Calculate initial canvas height percent to leave DEFAULT_NOTES_HEIGHT for notes
  const calculateInitialCanvasPercent = () => {
    const totalHeight = window.innerHeight;
    const availableHeight = totalHeight - NAVBAR_HEIGHT - TOOLBAR_HEIGHT;
    const canvasHeight = availableHeight - DEFAULT_NOTES_HEIGHT;
    return (canvasHeight / availableHeight) * 100;
  };
  
  const [canvasHeightPercent, setCanvasHeightPercent] = useState(calculateInitialCanvasPercent());
  const centerContentRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Handle resize dragging
  const handleResizeMouseMove = useCallback((e: MouseEvent) => {
    if (!centerContentRef.current) return;

    // Calculate available height: window height - navbar - toolbar
    const totalHeight = window.innerHeight;
    const availableHeight = totalHeight - NAVBAR_HEIGHT - TOOLBAR_HEIGHT;
    
    // Calculate mouse position relative to the start of canvas/notes area
    const containerRect = centerContentRef.current.getBoundingClientRect();
    const mouseY = e.clientY - containerRect.top;
    
    // Calculate new canvas percentage based on available height
    const newPercent = (mouseY / availableHeight) * 100;
    
    // Enforce constraints:
    // 1. Canvas must be at least 40% of available height
    // 2. Notes must have at least NOTES_MIN_HEIGHT pixels
    const maxCanvasPercent = ((availableHeight - NOTES_MIN_HEIGHT) / availableHeight) * 100;
    const constrainedPercent = Math.max(CANVAS_MIN_PERCENT, Math.min(newPercent, maxCanvasPercent));
    
    setCanvasHeightPercent(constrainedPercent);
    
    // Immediately trigger canvas resize during drag for live resizing
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, [NAVBAR_HEIGHT, TOOLBAR_HEIGHT, CANVAS_MIN_PERCENT, NOTES_MIN_HEIGHT]);

  const handleResizeMouseUp = useCallback(() => {
    setIsDragging(false);
    
    // Call resize complete callback to upgrade fabric canvas quality
    if (onResizeComplete) {
      // Small delay to ensure DOM has updated and resize event has triggered
      setTimeout(() => {
        logger.info('[SlidesLayout] Resize complete, triggering quality upgrade');
        onResizeComplete();
      }, 150);
    }
  }, [onResizeComplete]);

  // Calculate actual pixel heights based on percentages
  const calculateHeights = useCallback(() => {
    if (!centerContentRef.current) return { canvasHeight: 0, notesHeight: 0 };
    
    const totalHeight = window.innerHeight;
    const availableHeight = totalHeight - NAVBAR_HEIGHT - TOOLBAR_HEIGHT;
    
    const canvasHeight = Math.floor((canvasHeightPercent / 100) * availableHeight);
    const notesHeight = Math.floor(availableHeight - canvasHeight);
    
    return { canvasHeight, notesHeight };
  }, [canvasHeightPercent, NAVBAR_HEIGHT, TOOLBAR_HEIGHT]);

  const heights = calculateHeights();

  // Trigger window resize event when canvas height changes to recalculate canvas dimensions
  useEffect(() => {
    logger.info('[SlidesLayout] Canvas height changed:', heights.canvasHeight);
    // Dispatch resize event to trigger canvas recalculation
    const resizeEvent = new Event('resize');
    window.dispatchEvent(resizeEvent);
  }, [heights.canvasHeight]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleResizeMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleResizeMouseMove);
        document.removeEventListener('mouseup', handleResizeMouseUp);
      };
    }
  }, [isDragging, handleResizeMouseMove, handleResizeMouseUp]);

  // Select first scene if nothing is selected
  React.useEffect(() => {
    if (!selectedSceneId && sceneList.length > 0) {
      setSelectedSceneId(sceneList[0].id);
    }
  }, [sceneList, selectedSceneId, setSelectedSceneId]);

  // Sync currentScene atom whenever selectedSceneId or sceneList changes
  // This ensures the Retake button shows correctly after page refresh and after confirmation
  React.useEffect(() => {
    if (selectedSceneId) {
      // Use String() comparison to handle mixed number/string IDs
      const scene = sceneList.find(s => String(s.id) === String(selectedSceneId));
      if (scene) {
        setCurrentScene(scene);
      }
    }
  }, [selectedSceneId, sceneList, setCurrentScene]);

  // Custom scene select handler that reopens right sidebar when selecting a scene
  const handleSceneSelect = React.useCallback((sceneId: string) => {
    setSelectedSceneId(sceneId);
    
    // CRITICAL FIX: Also update currentSceneAtom so recording uses the correct scene!
    // Use String() comparison to handle mixed number/string IDs
    const scene = sceneList.find(s => String(s.id) === String(sceneId));
    if (scene) {
      setCurrentScene(scene);
    }
    
    // Reopen right sidebar when selecting a scene from left rail
    if (isRightSidebarCollapsed) {
      setIsRightSidebarCollapsed(false);
    }
  }, [setSelectedSceneId, setCurrentScene, sceneList, isRightSidebarCollapsed]);

  const selectedScene = sceneList.find(s => String(s.id) === String(selectedSceneId)) || currentScene;

  return (
    <div className="slides-layout">
      {/* Left Sidebar - Slides Rail */}
      <div className={`slides-rail-container ${isLeftSidebarCollapsed ? 'collapsed' : ''}`}>
        {!isLeftSidebarCollapsed && (
          <SlidesRail
            project={activeProject}
            scenes={sceneList}
            selectedSceneId={selectedSceneId}
            isUpdatingScene={isUpdatingScene}
            savingTransitionSceneId={savingTransitionSceneId}
            onSceneSelect={handleSceneSelect}
            onProjectInfoClick={() => {}}
            onProjectTitleChange={onProjectTitleChange}
            onAddScene={onAddScene}
            onRenameScene={onRenameScene}
            onDuplicateScene={onDuplicateScene}
            onDeleteScene={onDeleteScene}
            onReorderScenes={onReorderScenes}
            onSceneTitleChange={onSceneTitleChange}
            onSceneTagsChange={onSceneTagsChange}
            onSceneNotesChange={onSceneNotesChange}
            onCopySettings={onCopySettings}
            onJumpToRecording={onJumpToRecording}
            onEditSettings={onEditSettings}
            onExportScene={onExportScene}
            onResetVideo={onResetVideo}
            onPreviewScene={onPreviewScene}
            onConfirmScene={onConfirmScene}
            onEditTransition={onEditTransition}
            onPreviewTransition={onPreviewTransition}
          />
        )}
        <button
          className="sidebar-toggle sidebar-toggle-left"
          onClick={() => setIsLeftSidebarCollapsed(!isLeftSidebarCollapsed)}
          aria-label={isLeftSidebarCollapsed ? 'Expand slides rail' : 'Collapse slides rail'}
        >
          {isLeftSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Center - Canvas + Speaker Notes + Controls */}
      <div className="slides-center-content" ref={centerContentRef}>
        <div 
          className="canvas-container" 
          style={{ 
            height: `${heights.canvasHeight}px`,
            flexShrink: 0
          }}
        >
          <div className="canvas-aspect-wrapper">
            {canvasComponent}
          </div>
        </div>

        {/* Resize Handle */}
        <div 
          className={`resize-divider ${isDragging ? 'dragging' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
        >
          <div className="resize-handle">
            <div className="resize-indicator" />
          </div>
        </div>

        <SpeakerNotes
          sceneId={selectedSceneId || ''}
          scene={selectedScene}
          projectId={activeProject?.id}
          onNotesChange={onSceneNotesChange}
          style={{ 
            height: `${heights.notesHeight}px`,
            flexShrink: 0
          }}
        />

        <div className="recording-controls-container">
          {recordingControls}
        </div>
      </div>

      {/* Right Sidebar - Scene Details */}
      <div className={`scene-details-container ${isRightSidebarCollapsed ? 'collapsed' : ''}`}>
        {!isRightSidebarCollapsed && (
          <SceneDetailsPanel
            scene={selectedScene}
            project={activeProject}
            onSceneSelect={setSelectedSceneId}
            onClose={() => setIsRightSidebarCollapsed(true)}
            onSceneTagsChange={onSceneTagsChange}
            isUpdatingScene={isUpdatingScene}
            canvasRatio={canvasRatio}
            liveBackgroundColor={liveBackgroundColor}
            liveBackgroundImage={liveBackgroundImage}
            selectedBackground={selectedScene?.background}
            onPreviewScene={onPreviewScene}
            onConfirmScene={onConfirmScene}
            onRetakeScene={onRetakeScene}
            onDuplicateScene={onDuplicateScene}
            onDeleteScene={onDeleteScene}
            onExportScene={onExportScene}
            onOpenTeleprompter={handleOpenTeleprompter}
            onResetBackground={onResetBackground}
            onEditSettings={onEditSettings}
          />
        )}
        <button
          className="sidebar-toggle sidebar-toggle-right"
          onClick={() => setIsRightSidebarCollapsed(!isRightSidebarCollapsed)}
          aria-label={isRightSidebarCollapsed ? 'Expand scene details' : 'Collapse scene details'}
        >
          {isRightSidebarCollapsed ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>
    </div>
  );
};
